import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Plus, Check, X as XIcon, FileText } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { SalaryPreviewModal } from '@/pages/flatbed/SalaryPreviewModal';

interface Ledger { id: string; occurred_at: string; entry_type: string; amount_sar: string; description: string }
interface Reimbursable { id: string; submitted_at: string; category: string | null; amount_sar: string; description: string | null; status: string }
interface Driver { id: string; code: string | null; full_name_en: string | null; balance: string }
// PR MAY10 P4B-POLISH (A.2): driver_advances rows from /api/v1/driver-advances-v2
interface AdvanceRow {
  id: string; code: string;
  amount_sar: string; recovered_amount_sar: string | null;
  purpose: string | null; purpose_enum: string | null;
  description: string | null; status: string;
  recovery_method: string | null;
  requested_at: string | null; approved_at: string | null; paid_at: string | null;
  payment_method: string | null;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DriverFinanceDetailPage() {
  const { driverId } = useParams<{ driverId: string }>();
  const [driver, setDriver] = useState<Driver | null>(null);
  const [ledger, setLedger] = useState<Ledger[]>([]);
  const [reimb, setReimb] = useState<Reimbursable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdvance, setShowAdvance] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advanceNotes, setAdvanceNotes] = useState('');
  const [busy, setBusy] = useState(false);
  // PR MAY10 P4B-POLISH (A.2)
  const [tab, setTab] = useState<'overview' | 'advances' | 'ledger'>('overview');
  const [advances, setAdvances] = useState<AdvanceRow[]>([]);
  // PR-S2 MAY 11 — driver ledger view (advances + expenses paid from advances + net owed)
  const [ledgerView, setLedgerView] = useState<{
    advances: Array<Record<string, string | number | null>>;
    expensesPaidFromAdvances: Array<Record<string, string | number | null>>;
    netOwedSar: string;
  } | null>(null);
  // PR-RIYADH-FIX-3 MAY 12 — driver_expenses balance (new flow). Parallel to
  // the migration-051 reconciliation: maintenance is still counted there;
  // ad-hoc DEX rows (fuel/tolls/parts/...) are counted here.
  const [dexBalance, setDexBalance] = useState<{
    totalAdvancesSar: number;
    totalReconciledSar: number;
    netOwedSar: number;
    recentExpenses: Array<{
      id: string; code: string; expenseDate: string; category: string;
      amountSar: number; vehicleCode: string | null; vehiclePlate: string | null;
      status: string; advanceCode: string | null;
    }>;
  } | null>(null);
  // PR-S3 MAY 11 — salary preview modal
  const [salaryOpen, setSalaryOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!driverId) return;
    try {
      const r = await apiClient.get<{ driver: Driver; ledger: Ledger[]; reimbursables: Reimbursable[] }>('/api/v1/driver-finance/drivers/' + driverId);
      setDriver(r.data.driver);
      setLedger(r.data.ledger);
      setReimb(r.data.reimbursables);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [driverId]);

  useEffect(() => { refresh(); }, [refresh]);

  // PR MAY10 P4B-POLISH (A.2): pull driver_advances from v2 backend
  const refreshAdvances = useCallback(async () => {
    if (!driverId) return;
    try {
      const r = await apiClient.get<AdvanceRow[]>(`/api/v1/driver-advances-v2?driverId=${driverId}`);
      setAdvances(Array.isArray(r.data) ? r.data : []);
    } catch { /* keep previous on transient error */ }
  }, [driverId]);
  useEffect(() => { refreshAdvances(); }, [refreshAdvances]);

  // PR-S2 MAY 11 — fetch /drivers/:id/ledger on tab open
  const refreshLedger = useCallback(async () => {
    if (!driverId) return;
    try {
      const r = await apiClient.get<{
        advances: Array<Record<string, string | number | null>>;
        expensesPaidFromAdvances: Array<Record<string, string | number | null>>;
        netOwedSar: string;
      }>(`/api/v1/drivers/${driverId}/ledger`);
      setLedgerView(r.data);
    } catch { /* keep previous */ }
  }, [driverId]);
  useEffect(() => { if (tab === 'ledger') refreshLedger(); }, [tab, refreshLedger]);

  // PR-RIYADH-FIX-3: load DEX balance on Ledger tab open.
  const refreshDexBalance = useCallback(async () => {
    if (!driverId) return;
    try {
      const r = await apiClient.get<typeof dexBalance>(`/api/v1/drivers/${driverId}/balance`);
      setDexBalance(r.data);
    } catch { /* keep previous */ }
  }, [driverId]);
  useEffect(() => { if (tab === 'ledger') refreshDexBalance(); }, [tab, refreshDexBalance]);

  async function issueAdvance() {
    if (!driverId) return;
    const amt = Number(advanceAmount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    setBusy(true);
    try {
      await apiClient.post('/api/v1/driver-finance/advances', { driverId, amountSar: amt, notes: advanceNotes || undefined });
      setShowAdvance(false); setAdvanceAmount(''); setAdvanceNotes('');
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  async function reviewReimbursable(id: string, action: 'approve' | 'reject') {
    setBusy(true);
    try {
      await apiClient.post('/api/v1/driver-finance/reimbursables/' + id + '/' + action, action === 'reject' ? { rejectionReason: window.prompt('Reason?') ?? '' } : {});
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !driver) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground">{error ?? 'Not found'}</div>
      <Link to="/dashboard/flatbed/driver-accounts"><Button variant="outline">Back</Button></Link>
    </div>
  );

  const bal = Number(driver.balance);
  const pendingReimb = reimb.filter((r) => r.status === 'pending');
  // Outstanding advance = sum of (amount - recovered) for paid/approved advances
  const advTotalOutstanding = advances
    .filter(a => a.status === 'paid' || a.status === 'approved')
    .reduce((s, a) => s + Math.max(0, Number(a.amount_sar) - Number(a.recovered_amount_sar ?? 0)), 0);
  const advCount = advances.length;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard/flatbed/driver-accounts"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Driver accounts</Button></Link>
          <div className="text-sm font-medium">{driver.full_name_en ?? driver.code}</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        <div className="flex gap-2 border-b border-border">
          {([
            { v: 'overview', l: 'Overview' },
            { v: 'advances', l: `Advances (${advCount})` },
            { v: 'ledger',   l: 'Ledger' },
          ] as const).map((x) => (
            <button key={x.v} onClick={() => setTab(x.v)}
              className={'px-3 py-2 text-sm border-b-2 -mb-px ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {x.l}
            </button>
          ))}
        </div>

        {tab === 'overview' && (<>
        <div className="rounded-lg border border-border p-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Balance</div>
            <div className={'text-3xl font-light mt-1 ' + (bal >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400')}>{fmtSar(bal)}</div>
            <div className="text-xs text-muted-foreground mt-1">{bal >= 0 ? 'Company owes driver' : 'Driver owes company'}</div>
          </div>
          <div className="flex flex-col gap-2 items-stretch sm:items-end">
            <Button variant="gold" onClick={() => setShowAdvance((s) => !s)} className="gap-2"><Plus className="h-4 w-4" /> Issue advance</Button>
            {/* PR-S3 MAY 11 — salary preview + PDF */}
            <Button variant="outline" onClick={() => setSalaryOpen(true)} className="gap-2"><FileText className="h-4 w-4" /> Run salary</Button>
          </div>
        </div>

        {showAdvance && (
          <div className="rounded-lg border border-border p-3 space-y-2">
            <input type="number" value={advanceAmount} onChange={(e) => setAdvanceAmount(e.target.value)} placeholder="Amount SAR"
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm" />
            <input value={advanceNotes} onChange={(e) => setAdvanceNotes(e.target.value)} placeholder="Notes (optional)"
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm" />
            <div className="flex gap-2">
              <Button onClick={issueAdvance} disabled={busy} variant="gold" size="sm">Confirm advance</Button>
              <Button onClick={() => setShowAdvance(false)} variant="ghost" size="sm">Cancel</Button>
            </div>
          </div>
        )}

        {pendingReimb.length > 0 && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Pending reimbursables</div>
            <div className="space-y-2">
              {pendingReimb.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-sm">
                  <div className="flex-1">
                    <div>{r.category ?? '—'} · {fmtSar(Number(r.amount_sar))}</div>
                    <div className="text-xs text-muted-foreground">{r.description ?? ''}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => reviewReimbursable(r.id, 'approve')} disabled={busy}><Check className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => reviewReimbursable(r.id, 'reject')} disabled={busy}><XIcon className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Ledger</div>
          {ledger.length === 0 ? <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">No entries.</div>
          : <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/30 text-muted-foreground"><tr>
                  <th className="text-left px-3 py-2 font-normal">Date</th>
                  <th className="text-left px-3 py-2 font-normal">Type</th>
                  <th className="text-left px-3 py-2 font-normal">Description</th>
                  <th className="text-right px-3 py-2 font-normal">Amount</th>
                </tr></thead>
                <tbody>
                  {ledger.map((l) => {
                    const a = Number(l.amount_sar);
                    return (
                      <tr key={l.id} className="border-t border-border">
                        <td className="px-3 py-2">{new Date(l.occurred_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td className="px-3 py-2">{l.entry_type}</td>
                        <td className="px-3 py-2">{l.description}</td>
                        <td className={'px-3 py-2 text-right font-mono ' + (a >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400')}>{a >= 0 ? '+' : ''}{fmtSar(a)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>}
        </div>
        </>)}

        {tab === 'advances' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-4 flex items-baseline justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Outstanding advances</div>
                <div className={'text-2xl font-light mt-1 ' + (advTotalOutstanding > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground')}>{fmtSar(advTotalOutstanding)}</div>
              </div>
              <Link to="/dashboard/cashflow/advances">
                <Button variant="outline" size="sm" className="gap-1"><Plus className="h-4 w-4" /> New advance</Button>
              </Link>
            </div>
            {advances.length === 0 ? (
              <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">No advances on record.</div>
            ) : (
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/30 text-muted-foreground"><tr>
                    <th className="text-left px-3 py-2 font-normal">Code</th>
                    <th className="text-right px-3 py-2 font-normal">Amount</th>
                    <th className="text-right px-3 py-2 font-normal">Recovered</th>
                    <th className="text-left px-3 py-2 font-normal">Purpose</th>
                    <th className="text-left px-3 py-2 font-normal">Status</th>
                    <th className="text-left px-3 py-2 font-normal">Recovery</th>
                    <th className="text-left px-3 py-2 font-normal">Paid at</th>
                  </tr></thead>
                  <tbody>
                    {advances.map((a) => {
                      const outstanding = Math.max(0, Number(a.amount_sar) - Number(a.recovered_amount_sar ?? 0));
                      return (
                        <tr key={a.id} className="border-t border-border">
                          <td className="px-3 py-2 font-mono">{a.code}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtSar(Number(a.amount_sar))}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-400">
                            {fmtSar(Number(a.recovered_amount_sar ?? 0))}
                            {outstanding > 0 && <span className="text-muted-foreground"> · {fmtSar(outstanding)} left</span>}
                          </td>
                          <td className="px-3 py-2">{(a.purpose_enum ?? a.purpose ?? '—').replace(/_/g, ' ')}</td>
                          <td className="px-3 py-2">{a.status.replace(/_/g, ' ')}</td>
                          <td className="px-3 py-2 text-muted-foreground">{a.recovery_method ?? '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground">{a.paid_at ? new Date(a.paid_at).toLocaleDateString('en-GB') : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'ledger' && (
          <div className="space-y-3">
            {dexBalance && (
              <div className="rounded-lg border border-border p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Advances vs driver-paid expenses</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div className="rounded-md border border-border p-3 bg-muted/10">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Total advances</div>
                    <div className="text-xl font-light font-mono mt-0.5">{fmtSar(dexBalance.totalAdvancesSar)}</div>
                  </div>
                  <div className="rounded-md border border-border p-3 bg-muted/10">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Reconciled expenses</div>
                    <div className="text-xl font-light font-mono mt-0.5 text-emerald-700 dark:text-emerald-400">−{fmtSar(dexBalance.totalReconciledSar)}</div>
                  </div>
                  <div className="rounded-md border border-border p-3 bg-muted/10">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Net owed by driver</div>
                    <div className={'text-xl font-mono font-medium mt-0.5 ' + (dexBalance.netOwedSar > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground')}>
                      {fmtSar(dexBalance.netOwedSar)}
                    </div>
                  </div>
                </div>
                {dexBalance.recentExpenses.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Recent expenses</div>
                    <div className="rounded-md border border-border overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/20 text-muted-foreground"><tr>
                          <th className="text-left px-3 py-2 font-normal">Code</th>
                          <th className="text-left px-3 py-2 font-normal">Date</th>
                          <th className="text-left px-3 py-2 font-normal">Category</th>
                          <th className="text-left px-3 py-2 font-normal">Vehicle</th>
                          <th className="text-left px-3 py-2 font-normal">Advance</th>
                          <th className="text-right px-3 py-2 font-normal">Amount</th>
                          <th className="text-left px-3 py-2 font-normal">Status</th>
                        </tr></thead>
                        <tbody>
                          {dexBalance.recentExpenses.map((e) => (
                            <tr key={e.id} className="border-t border-border">
                              <td className="px-3 py-2 font-mono">{e.code}</td>
                              <td className="px-3 py-2">{e.expenseDate}</td>
                              <td className="px-3 py-2 capitalize">{e.category}</td>
                              <td className="px-3 py-2">{e.vehiclePlate ?? e.vehicleCode ?? '—'}</td>
                              <td className="px-3 py-2 font-mono">{e.advanceCode ?? '—'}</td>
                              <td className="px-3 py-2 text-right font-mono">{fmtSar(e.amountSar)}</td>
                              <td className="px-3 py-2">{e.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!ledgerView ? (
              <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : (
              <>
                <div className="rounded-lg border border-border p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Net owed (maintenance reconciliation)</div>
                    <div className={'text-3xl font-light mt-1 ' + (Number(ledgerView.netOwedSar) > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground')}>
                      {fmtSar(Number(ledgerView.netOwedSar))}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Sum of every active advance's effective balance (amount − recovered − maintenance reconciled).
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-border overflow-hidden">
                  <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
                    Advances given ({ledgerView.advances.length})
                  </div>
                  {ledgerView.advances.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">No advances.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-muted/20 text-muted-foreground"><tr>
                        <th className="text-left px-3 py-2 font-normal">Code</th>
                        <th className="text-left px-3 py-2 font-normal">When</th>
                        <th className="text-right px-3 py-2 font-normal">Amount</th>
                        <th className="text-right px-3 py-2 font-normal">Recovered</th>
                        <th className="text-right px-3 py-2 font-normal">Reconciled</th>
                        <th className="text-right px-3 py-2 font-normal">Remaining</th>
                        <th className="text-left px-3 py-2 font-normal">Status</th>
                      </tr></thead>
                      <tbody>
                        {ledgerView.advances.map((a) => (
                          <tr key={String(a.id)} className="border-t border-border">
                            <td className="px-3 py-2 font-mono">{String(a.code ?? '—')}</td>
                            <td className="px-3 py-2">{a.given_at ? new Date(String(a.given_at)).toLocaleDateString('en-GB') : (a.advance_date ? new Date(String(a.advance_date)).toLocaleDateString('en-GB') : '—')}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtSar(Number(a.amount_sar ?? 0))}</td>
                            <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-400">{fmtSar(Number(a.recovered_amount_sar ?? 0))}</td>
                            <td className="px-3 py-2 text-right font-mono text-sky-700 dark:text-sky-400">{fmtSar(Number(a.reconciled_expenses_sar ?? 0))}</td>
                            <td className="px-3 py-2 text-right font-mono font-medium">{fmtSar(Number(a.effective_balance_sar ?? 0))}</td>
                            <td className="px-3 py-2">{String(a.status ?? '—').replace(/_/g, ' ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="rounded-lg border border-border overflow-hidden">
                  <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
                    Expenses paid from advances ({ledgerView.expensesPaidFromAdvances.length})
                  </div>
                  {ledgerView.expensesPaidFromAdvances.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">No expenses reconciled against advances yet.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-muted/20 text-muted-foreground"><tr>
                        <th className="text-left px-3 py-2 font-normal">Date</th>
                        <th className="text-left px-3 py-2 font-normal">Event</th>
                        <th className="text-left px-3 py-2 font-normal">Vehicle</th>
                        <th className="text-left px-3 py-2 font-normal">Advance</th>
                        <th className="text-right px-3 py-2 font-normal">Amount</th>
                        <th className="text-left px-3 py-2 font-normal">Status</th>
                      </tr></thead>
                      <tbody>
                        {ledgerView.expensesPaidFromAdvances.map((e) => (
                          <tr key={String(e.id)} className="border-t border-border">
                            <td className="px-3 py-2">{e.reported_at ? new Date(String(e.reported_at)).toLocaleDateString('en-GB') : '—'}</td>
                            <td className="px-3 py-2 font-mono">{String(e.code ?? '—')}</td>
                            <td className="px-3 py-2">{String(e.vehicle_code ?? '—')}{e.vehicle_plate ? ' · ' + String(e.vehicle_plate) : ''}</td>
                            <td className="px-3 py-2 font-mono">{String(e.advance_code ?? '—')}</td>
                            <td className="px-3 py-2 text-right font-mono text-rose-700 dark:text-rose-400">−{fmtSar(Number(e.total_cost_sar ?? 0))}</td>
                            <td className="px-3 py-2">{String(e.status ?? '—').replace(/_/g, ' ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </main>
      {driverId && <SalaryPreviewModal open={salaryOpen} onOpenChange={setSalaryOpen} driverId={driverId} onCommitted={refresh} />}
    </div>
  );
}
